import { useMutation } from '@connectrpc/connect-query'
import { withdrawBiddingIntradayOrders } from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'

import { biddingQueryKeys } from '~/hooks/bidding/queryKeys'
import { useWorkflowCaseScopedMutation } from '~/hooks/bidding/useWorkflowCaseScopedMutation'
import type { InvalidatingMutationResultFor } from '~/hooks/useInvalidatingMutation'

/**
 * 時間前の札の書き込みで読み直す query (一覧・詳細・板発注のコマリボンの件数 AC-BIN-025)。
 * 1 件の状態遷移 (`useTransitionBiddingIntradayOrderState`) と同じ組。
 */
const INTRADAY_ORDER_INVALIDATION_KEYS = [
  biddingQueryKeys.intradayList,
  biddingQueryKeys.intradayDetail,
  biddingQueryKeys.intradayActiveOrderCounts,
] as const

/** 時間前の札の一覧・詳細・件数を読み直す。 */
export const invalidateIntradayOrderQueries = async (
  queryClient: QueryClient,
): Promise<void> => {
  await Promise.all(
    INTRADAY_ORDER_INVALIDATION_KEYS.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  )
}

/**
 * 入札中の札の一括板取消 (AC-BIN-112) mutation hook。 操作元のワークフローケース ID は
 * `useWorkflowCaseScopedMutation` が載せる (完了済み・削除済みのケースは backend が全体を拒否する)。
 *
 * 成功・失敗のどちらでも一覧を読み直す (失敗は 1 tx でどの札も取り消していないが、 一覧の
 * 値を確かめ直して次の操作に備える)。 読み直しは応答を返す前に
 * 終える (送信中だった一覧の取得は読み直しが取り消すので、 受け付けた後の観測に受付前の値が
 * 混ざらない。 `useBulkResultNotice`)。
 */
export function useWithdrawBiddingIntradayOrders(): InvalidatingMutationResultFor<
  typeof withdrawBiddingIntradayOrders
> {
  const queryClient = useQueryClient()
  return useWorkflowCaseScopedMutation(
    useMutation(withdrawBiddingIntradayOrders, {
      onSettled: () => invalidateIntradayOrderQueries(queryClient),
    }),
  )
}
